import type { Course } from "@brookbush/types";

import clsx from "clsx";
import { memo } from "react";

import Skeleton from "~/components/Skeleton";
import { useCourseProgress } from "~/hooks";

interface CourseProgressProps {
	course: Course;
	hasCourseAccess: boolean;
	isLoadingCourseAccess: boolean;
}

const CourseProgress = ({
	course,
	hasCourseAccess,
	isLoadingCourseAccess,
}: CourseProgressProps) => {
	const { progress } = useCourseProgress(course);

	const eligibleSections = course.sections.slice(1, course.sections.length - 2);

	const completedSections = eligibleSections.filter((section) =>
		progress?.find((item) => item.sectionKey === section._key),
	);

	const completedValue =
		eligibleSections.length &&
		(completedSections.length / eligibleSections.length) * 100;

	return (
		<div className="col-span-full laptop:col-span-7 phablet:-my-1 flex w-full items-center gap-6 phablet:gap-12">
			<div className="flex flex-1 flex-col gap-1.5 phablet:gap-2.5">
				{isLoadingCourseAccess ? (
					<Skeleton className="my-1 h-3 w-44" />
				) : !hasCourseAccess ? (
					<p className="font-medium text-sm">
						Become a member to get unlimited access.
					</p>
				) : (
					<p className="font-medium text-sm">
						{completedSections.length} / {eligibleSections.length} Sections{" "}
						Completed
					</p>
				)}
				<div className="mt-1 phablet:mb-0.5 h-2.5 w-full rounded-full bg-slate-100 shadow-inner dark:bg-slate-700">
					<div
						className={clsx(
							"h-full rounded-full bg-emerald-400 shadow shadow-emerald-700/10 transition-all dark:shadow-slate-900",
						)}
						style={{
							width: `${completedValue}%`,
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export default memo(CourseProgress);
